import { TOO_MANY_ATTEMPTS_MESSAGE } from '@shared/core/bookings'
import { extractErrorMessage } from '@shared/util'
import { format } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useAppointmentTypes,
  useComputeSlotsForCustomerDay,
  useCreateBooking,
  useProviderDetails,
} from 'src/modules/booking/hooks'
import type { CustomerAddressHandOffState } from 'src/modules/booking/types/customer-address-hand-off.types'
import { getBookingFormOutdatedMessage } from 'src/modules/booking/utils/booking-form-outdated'
import { isCalendarRefusedError } from 'src/modules/booking/utils/calendar-refused'
import { isTooManyRequestsError } from 'src/modules/booking/utils/too-many-requests'
import { calendarRefusedMessage } from '../booking-date-and-time-step/booking-date-and-time-step.domain'
import {
  computeEndTimeDisplay,
  findSlotByDisplayTime,
  getAppointmentTypeLabel,
  getEmailValidationError,
  getFullNameValidationError,
  getPhoneNumberValidationError,
  isValidEmail,
  isValidFullName,
  isValidPhoneNumber,
  requiresCustomerAddress,
} from './booking-details-step.domain'
import type { BookingDetailsStepProps } from './booking-details-step.interface'
import { useBookingQuestions } from './use-booking-questions'
import { useCustomerAddressField } from './use-customer-address-field'

const FULL_NAME_STORAGE_KEY = 'booking-full-name'
const PHONE_NUMBER_STORAGE_KEY = 'booking-phone-number'
const EMAIL_STORAGE_KEY = 'booking-email'

export function useBookingDetailsStep({
  providerId,
  slug,
  onBack,
  onAnswerChange,
  initialData,
}: BookingDetailsStepProps) {
  const navigate = useNavigate()
  const providerIdOrEmpty = providerId ?? ''
  const {
    fullName: initialFullName,
    phoneNumber: initialPhone,
    email: initialEmail,
    selectedDate,
    selectedTime,
    timezone,
    appointmentType: appointmentTypeId,
    bookingForm,
    answers,
  } = initialData ?? {}

  const { data: providerDetails } = useProviderDetails(providerIdOrEmpty, { enabled: !!providerId })
  const providerName = providerDetails?.full_name ?? 'Provider'

  const { data: appointmentTypes = [] } = useAppointmentTypes(providerIdOrEmpty, { enabled: !!providerId })
  const appointmentTypeLabel = useMemo(
    () => getAppointmentTypeLabel(appointmentTypes, appointmentTypeId),
    [appointmentTypes, appointmentTypeId],
  )
  const selectedAppointmentType = appointmentTypes.find((t) => t.id === appointmentTypeId)
  const addressField = useCustomerAddressField(requiresCustomerAddress(selectedAppointmentType))
  const { validate: validateAddress, submittedValue: customerAddress } = addressField
  const questionsField = useBookingQuestions({ form: bookingForm, answers, onAnswerChange })
  const { validate: validateQuestions, submission: bookingFormSubmission } = questionsField

  const [fullName, setFullName] = useState(() => getStoredOrInitial(FULL_NAME_STORAGE_KEY, initialFullName))
  const [phoneNumber, setPhoneNumber] = useState(() => getStoredOrInitial(PHONE_NUMBER_STORAGE_KEY, initialPhone))
  const [email, setEmail] = useState(() => getStoredOrInitial(EMAIL_STORAGE_KEY, initialEmail))
  const [fullNameError, setFullNameError] = useState<string | null>(null)
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isRefusedOnSubmit, setIsRefusedOnSubmit] = useState(false)

  useEffect(() => {
    try {
      if (fullName) localStorage.setItem(FULL_NAME_STORAGE_KEY, fullName)
      else localStorage.removeItem(FULL_NAME_STORAGE_KEY)
    } catch {
      // ignore
    }
  }, [fullName])

  useEffect(() => {
    try {
      if (phoneNumber) localStorage.setItem(PHONE_NUMBER_STORAGE_KEY, phoneNumber)
      else localStorage.removeItem(PHONE_NUMBER_STORAGE_KEY)
    } catch {
      // ignore
    }
  }, [phoneNumber])

  useEffect(() => {
    try {
      if (email) localStorage.setItem(EMAIL_STORAGE_KEY, email)
      else localStorage.removeItem(EMAIL_STORAGE_KEY)
    } catch {
      // ignore
    }
  }, [email])

  const customerDateISO = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''
  const { data: slotsData, error: slotsError } = useComputeSlotsForCustomerDay(
    {
      providerId: providerIdOrEmpty,
      appointmentTypeId: appointmentTypeId ?? '',
      customerDateISO,
      customerTz: timezone ?? 'UTC',
    },
    {
      enabled: !!selectedDate && !!providerId && !!appointmentTypeId && !!timezone,
    },
  )

  // A refused calendar is permanent, so it outlives field edits and keeps Confirm disabled.
  const isCalendarRefused = isRefusedOnSubmit || isCalendarRefusedError(slotsError)

  const selectedSlot = useMemo(() => {
    if (!slotsData?.slots || !selectedTime) return null
    return findSlotByDisplayTime(slotsData.slots, selectedTime, timezone ?? 'UTC')
  }, [slotsData?.slots, selectedTime, timezone])

  const createBookingMutation = useCreateBooking()
  const durationMinutes = selectedAppointmentType?.duration_minutes ?? 30
  const endTimeDisplayForSummary = useMemo(
    () => (selectedTime ? computeEndTimeDisplay(selectedTime, durationMinutes) : ''),
    [selectedTime, durationMinutes],
  )

  const handleFullNameChange = useCallback((value: string) => {
    setFullName(value)
    setFullNameError(null)
    setErrorMessage(null)
  }, [])

  const handleFullNameBlur = useCallback(() => {
    setFullNameError(getFullNameValidationError(fullName))
  }, [fullName])

  const handlePhoneNumberChange = useCallback((value: string) => {
    setPhoneNumber(value)
    setPhoneNumberError(null)
    setErrorMessage(null)
  }, [])

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    setEmailError(null)
    setErrorMessage(null)
  }, [])

  const handleEmailBlur = useCallback(() => {
    setEmailError(getEmailValidationError(email))
  }, [email])

  const validateAll = useCallback((): boolean => {
    const fn = getFullNameValidationError(fullName)
    const pn = getPhoneNumberValidationError(phoneNumber)
    const em = getEmailValidationError(email)
    const isAddressValid = validateAddress()
    const areQuestionsValid = validateQuestions()
    setFullNameError(fn)
    setPhoneNumberError(pn)
    setEmailError(em)
    return !fn && !pn && !em && isAddressValid && areQuestionsValid
  }, [fullName, phoneNumber, email, validateAddress, validateQuestions])

  const handleConfirm = useCallback(async () => {
    if (!providerId || !appointmentTypeId || !selectedDate || !selectedTime || !selectedSlot) {
      setErrorMessage('Please complete all required fields')
      return
    }
    if (!validateAll()) return
    setErrorMessage(null)

    try {
      const booking = await createBookingMutation.mutateAsync({
        provider_id: providerId,
        appointment_type_id: appointmentTypeId,
        start_time_utc: selectedSlot.start_time_utc,
        customer_name: fullName,
        customer_phone: phoneNumber,
        customer_email: email.trim(),
        customer_tz: timezone ?? undefined,
        ...(customerAddress ? { customer_address: customerAddress } : {}),
        ...bookingFormSubmission,
      })
      const handOff: CustomerAddressHandOffState | null = customerAddress ? { customerAddress } : null
      navigate(`/booking/${providerId}/${slug}/${booking.id}`, { state: handOff })
    } catch (error) {
      if (isCalendarRefusedError(error)) setIsRefusedOnSubmit(true)
      else if (isTooManyRequestsError(error)) setErrorMessage(TOO_MANY_ATTEMPTS_MESSAGE)
      else
        setErrorMessage(
          getBookingFormOutdatedMessage(error) ??
            extractErrorMessage(error, 'Failed to create booking. Please try again.'),
        )
    }
  }, [
    providerId,
    slug,
    appointmentTypeId,
    selectedDate,
    selectedTime,
    selectedSlot,
    fullName,
    phoneNumber,
    email,
    timezone,
    customerAddress,
    bookingFormSubmission,
    validateAll,
    createBookingMutation,
    navigate,
  ])

  const canConfirm =
    !!fullName &&
    !!phoneNumber &&
    !!email &&
    !!selectedSlot &&
    isValidFullName(fullName) &&
    isValidPhoneNumber(phoneNumber) &&
    isValidEmail(email) &&
    // An untouched empty address leaves Confirm enabled: clicking it is how the client learns what is missing.
    !addressField.error &&
    !fullNameError &&
    !phoneNumberError &&
    !emailError &&
    !questionsField.hasErrors &&
    !createBookingMutation.isLoading &&
    !isCalendarRefused

  return {
    providerName,
    appointmentTypeLabel,
    selectedDate,
    selectedTime,
    timezone,
    fullName,
    phoneNumber,
    email,
    fullNameError,
    phoneNumberError,
    emailError,
    errorMessage: isCalendarRefused ? calendarRefusedMessage(false) : errorMessage,
    endTimeDisplayForSummary,
    isConfirmLoading: createBookingMutation.isLoading,
    canConfirm,
    onBack,
    handleFullNameChange,
    handleFullNameBlur,
    handlePhoneNumberChange,
    handleEmailChange,
    handleEmailBlur,
    handleConfirm,
    addressField,
    questionsField,
  }
}

function getStoredOrInitial(storageKey: string, initialValue?: string): string {
  try {
    const saved = localStorage.getItem(storageKey)
    if (saved) return saved
  } catch {
    // ignore
  }
  return initialValue ?? ''
}
